import { ShowRoom } from '../models'
import { LATITUDE_PLACEHOLDER, LONGITUDE_PLACEHOLDER } from '../types'
import { object, string } from 'yup'
import {
  ONLY_DIGITS_AND_LETTERS_REGEX,
  AT_LEAST_ONE_DIGIT_AND_ONE_LETTER_REGEX,
} from 'src/Libs/installation-statement/hooks/validation/regex-patterns'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ShowRoomForm } from '../models/showroom.model'
import { FormInitializerReturnType, stringSchema } from 'src/Configuration/validation'

const MAX_ADDRESS_LONG_FIELD_LENGTH = 255
const MAX_ADDRESS_SHORT_FIELD_LENGTH = 25

/**
 * Initializes the form for editing a company showroom.
 * @param model - Showroom | null
 * @returns initialValues and validationSchema for company showroom form
 */
export const useCompanyShowroomFormInitializer = (model: ShowRoom | null): FormInitializerReturnType<ShowRoomForm, ShowRoom> => {
  const { t } = useCustomTranslation()
  const stringSchemaWithTranslation = stringSchema(t)

  const initialValues: ShowRoom = {
    address: model?.address ?? '',
    city: model?.city ?? '',
    countryCode: model?.countryCode ?? '',
    email: model?.email ?? '',
    fax: model?.fax ?? '',
    gpsLatitude: model?.gpsLatitude ?? LATITUDE_PLACEHOLDER,
    gpsLongitude: model?.gpsLongitude ?? LONGITUDE_PLACEHOLDER,
    id: model?.id,
    name: model?.name ?? '',
    phone: model?.phone ?? '',
    postalCode: model?.postalCode ?? '',
    website: model?.website ?? '',
  }

  const validationSchema = object().shape({
    name: stringSchemaWithTranslation({
      regexp: { rule: ONLY_DIGITS_AND_LETTERS_REGEX, errorMessage: t('validation.error-message.only-letters-and-digits-allowed') },
    }).required(),
    address: stringSchemaWithTranslation({
      regexp: { rule: AT_LEAST_ONE_DIGIT_AND_ONE_LETTER_REGEX, errorMessage: t('validation.error-message.must-contain-one-digit-and-one-letter') },
      maxLength: MAX_ADDRESS_LONG_FIELD_LENGTH,
    }).required(),
    postalCode: stringSchemaWithTranslation({
      regexp: { rule: ONLY_DIGITS_AND_LETTERS_REGEX, errorMessage: t('validation.error-message.only-letters-and-digits-allowed') },
      maxLength: 25,
    }).required(),
    city: stringSchemaWithTranslation({
      regexp: { rule: ONLY_DIGITS_AND_LETTERS_REGEX, errorMessage: t('validation.error-message.only-letters-and-digits-allowed') },
      maxLength: 25,
    }).required(),
    countryCode: stringSchemaWithTranslation({ maxLength: MAX_ADDRESS_SHORT_FIELD_LENGTH }).required(),
    phone: stringSchemaWithTranslation({ maxLength: MAX_ADDRESS_SHORT_FIELD_LENGTH }),
    fax: stringSchemaWithTranslation({ maxLength: MAX_ADDRESS_LONG_FIELD_LENGTH }),
    email: string().email(),
    website: string()
      .transform((value: string) => (value.startsWith('https://') ? value : ' '))
      .url(t('validations.form.url.is-url')),
  })
  return { initialValues, validationSchema }
}
